// ============================================
// POSTHOG ANALYTICS
// ============================================
//
// Privacy-first PostHog wrapper for the Reserve / Client proposal portal.
//
// Design goals:
//  - Zero impact when unconfigured: if REACT_APP_POSTHOG_KEY is absent (e.g. local
//    dev), every function here becomes a safe no-op and the app behaves exactly as
//    before.
//  - Strict privacy by default: session replays mask ALL text and inputs so we never
//    record client pricing, names, or contact details. Relax later per-element once
//    you've decided what is safe to show (see MASKING note below).
//  - Single source of truth: the app's existing analyticsService funnels every action
//    through trackAction(); we mirror those calls here so PostHog and the Google Sheets
//    log stay in sync without scattering capture() calls across the codebase.

import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let enabled = false;

// Redact sensitive query params from any URL-like property before it leaves the browser.
function scrubUrl(value) {
  if (typeof value !== 'string') return value;
  return value.replace(
    /([?&](?:password|pwd|token|clienttoken|email|apikey|key)=)[^&#]*/gi,
    '$1[redacted]'
  );
}

function sanitizeProperties(properties) {
  if (!properties || typeof properties !== 'object') return properties;
  for (const urlProp of ['$current_url', '$referrer', '$pathname', '$referring_domain']) {
    if (properties[urlProp]) properties[urlProp] = scrubUrl(properties[urlProp]);
  }
  return properties;
}

// Initialize PostHog once, before the app renders. Safe to call when no key is set.
export function initPostHog() {
  if (enabled) return;
  if (!POSTHOG_KEY) {
    // Unconfigured (local dev or key not yet provisioned) -> stay a no-op.
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Only create durable person profiles for logged-in clients we explicitly identify.
    person_profiles: 'identified_only',
    // We drive our own "view_section" events (state-based SPA, no react-router), so we
    // don't want PostHog's automatic pageview firing on a URL that never changes.
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: false,
    session_recording: {
      // MASKING: strict launch defaults. Every text node and input is masked so no
      // pricing / PII is ever recorded. To selectively reveal safe, non-sensitive UI
      // later, narrow maskTextSelector to a specific selector (e.g. '[data-sensitive]')
      // and tag only the sensitive nodes, rather than masking everything.
      maskAllInputs: true,
      maskTextSelector: '*',
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
        text: true,
        number: true,
      },
    },
    sanitize_properties: sanitizeProperties,
    loaded: () => {
      enabled = true;
    },
  });

  enabled = true;
}

// Tie the current session to a known client after login.
export function identifyClient(clientInfo) {
  if (!enabled || !clientInfo) return;
  const distinctId = clientInfo.email || clientInfo.memberId;
  if (!distinctId) return;
  try {
    posthog.identify(distinctId, {
      email: clientInfo.email,
      name: clientInfo.fullName,
      company: clientInfo.clientCompanyName,
      is_trial: clientInfo.isTrialAccount === true,
      tier: clientInfo.tier,
      portal: 'reserve',
    });
    if (clientInfo.clientCompanyName) {
      // Group analytics by company so you can analyze account-level behavior.
      posthog.group('company', clientInfo.clientCompanyName, {
        name: clientInfo.clientCompanyName,
      });
    }
  } catch (e) {
    /* analytics must never break the app */
  }
}

// Clear identity on logout so the next user on a shared device isn't merged in.
export function resetClient() {
  if (!enabled) return;
  try {
    posthog.reset();
  } catch (e) {
    /* no-op */
  }
}

// Mirror an app action (from analyticsService.trackAction) into PostHog.
export function captureEvent(action, page = '', details = '') {
  if (!enabled || !action) return;
  try {
    posthog.capture(action, {
      page: page || undefined,
      // details is an id/status/section label (never PII) in this app's usage.
      details: details === '' ? undefined : details,
    });
  } catch (e) {
    /* no-op */
  }
}

export default posthog;
